<script lang="ts" setup>
/**
 * Stripe 内嵌支付弹窗（Embedded Checkout）
 *
 * 网页内（/pay、章节弹窗）与游戏内（/game iframe）复用同一组件。
 * 通过 clientSecret 初始化 Stripe 官方内嵌表单，支付完成 onComplete 后 emit('complete')，
 * 由调用方负责验单与刷新；用户主动关闭则 emit('cancel')。
 */
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { useToastStore } from '@/stores'

const props = defineProps<{
  value: boolean
  clientSecret: string
  /** 支付完成后延迟多少毫秒再 emit complete，让 Stripe 成功文案有时间展示（默认 2000） */
  completeDelay?: number
}>()

const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'complete'): void
  (e: 'cancel'): void
}>()

const { $toast } = useToastStore()

const STRIPE_PK = import.meta.env.VITE_STRIPE_PK

// 单例缓存 Stripe.js 加载结果
let stripePromise: Promise<Stripe | null> | null = null
function getStripe() {
  if (!stripePromise)
    stripePromise = loadStripe(STRIPE_PK)
  return stripePromise
}

const mountEl = ref<HTMLElement | null>(null)
const loading = ref(false)
let checkout: StripeEmbeddedCheckout | null = null
// 标记本次支付是否已完成，用于区分「完成关闭」与「用户取消关闭」
let completed = false
// onComplete 的延迟 emit 定时器：仅组件卸载时清理，弹窗关闭不清，
// 以保证「完成态延迟期内用户手动关闭」仍能正常触发 complete 验单
let completeTimer: ReturnType<typeof setTimeout> | null = null

function clearCompleteTimer() {
  if (completeTimer) {
    clearTimeout(completeTimer)
    completeTimer = null
  }
}

function destroyCheckout() {
  if (checkout) {
    try {
      checkout.destroy()
    }
    catch (e) {
      console.warn('[STRIPE] destroy checkout error:', e)
    }
    checkout = null
  }
}

async function initCheckout() {
  if (!props.clientSecret)
    return

  if (!STRIPE_PK) {
    console.error('[STRIPE] VITE_STRIPE_PK 未配置，无法初始化 Embedded Checkout')
    $toast('Stripe configuration missing')
    emit('input', false)
    return
  }

  destroyCheckout()
  // 重置上一单可能残留的延迟定时器，避免串单误触发 complete
  clearCompleteTimer()
  loading.value = true
  completed = false
  try {
    const stripe = await getStripe()
    if (!stripe)
      throw new Error('Stripe.js failed to load')

    // 等待弹窗 DOM 渲染出挂载节点
    await nextTick()

    checkout = await stripe.initEmbeddedCheckout({
      clientSecret: props.clientSecret,
      onComplete: () => {
        completed = true
        // 延迟关闭：让 Stripe 内嵌表单的「支付成功」文案展示一会儿再交给调用方处理
        completeTimer = setTimeout(() => emit('complete'), props.completeDelay ?? 2000)
      },
    })

    if (mountEl.value)
      checkout.mount(mountEl.value)
  }
  catch (e: any) {
    console.error('[STRIPE] initEmbeddedCheckout 失败:', e)
    $toast(e?.message || 'Failed to load payment')
    destroyCheckout()
    emit('input', false)
  }
  finally {
    loading.value = false
  }
}

// 弹窗关闭（点击遮罩 / 关闭按钮）
function handleClose() {
  destroyCheckout()
  emit('input', false)
  if (!completed)
    emit('cancel')
}

// 仅监听 value：clientSecret 总是由调用方先于 value=true 设好，
// value 由 false → true 时初始化一次，关闭时销毁，从源头保证只 init 一次
watch(
  () => props.value,
  (val) => {
    if (val)
      initCheckout()
    else
      destroyCheckout()
  },
)

onBeforeUnmount(() => {
  clearCompleteTimer()
  destroyCheckout()
})
</script>

<template>
  <BasicPopup :value="value" @input="handleClose">
    <div class="stripe-checkout-popup bg-n-bg-card">
      <div class="flex items-center justify-between px-4 py-3 border-b border-n-border-primary">
        <span class="text-16px font-bold text-n-text-1">Checkout</span>
        <span class="text-22px leading-none text-n-text-2 cursor-pointer" @click="handleClose">×</span>
      </div>

      <div v-if="loading" class="h-200px flex items-center justify-center">
        <BasicSpinner class="mx-auto" />
      </div>

      <div ref="mountEl" class="stripe-mount" />
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.stripe-checkout-popup {
  overflow: hidden;
}

.stripe-mount {
  width: 100%;
  max-height: 75vh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
